/**
 * SEO Configuration and Utilities
 * Provides route-specific meta tags and Open Graph data
 */

import { ASSET_VERSIONS, getVersionedAsset } from './constants';
import { WALKTHROUGHS } from '../components/WalkthroughVideo';

const BASE_URL = 'https://finance.solidly.ai';
const SITE_NAME = 'Solidly Finance';

// Default meta tags for the site
export const defaultMeta = {
  title: 'Solidly Finance - Chart Your Path to Financial Goals',
  description: 'Plan your journey to financial freedom. Track debt payoff, build wealth to $100K and $1M, and achieve FIRE with our comprehensive personal finance planning tool.',
  keywords: 'personal finance, financial planning, debt payoff, wealth building, FIRE calculator, financial independence, budget tracker, net worth tracker',
  ogImage: `${BASE_URL}${getVersionedAsset('/og-image.jpg', ASSET_VERSIONS.OG_IMAGE)}`,
  twitterImage: `${BASE_URL}${getVersionedAsset('/twitter-image.jpg', ASSET_VERSIONS.TWITTER_IMAGE)}`,
};

// Route-specific SEO data
export const routeSEO = {
  '/': {
    title: 'Solidly Finance - Chart Your Path to Financial Goals',
    description: 'Plan your journey to financial freedom. Track debt payoff, build wealth to $100K and $1M, and achieve FIRE with our comprehensive personal finance planning tool.',
    keywords: 'personal finance, financial planning, debt payoff, wealth building, FIRE calculator, financial independence',
    canonical: BASE_URL,
  },
  '/downloads': {
    title: 'Download Solidly Finance - Desktop App for Mac & Windows',
    description: 'Download Solidly Finance desktop app for macOS and Windows. Secure, private financial planning software to track your path to financial independence.',
    keywords: 'download financial planning app, personal finance software, mac finance app, windows finance app, desktop finance tracker',
    canonical: `${BASE_URL}/downloads`,
  },
  '/releases': {
    title: 'Release Notes - Solidly Finance Updates & Changelog',
    description: 'View the latest updates, features, and improvements to Solidly Finance. Stay informed about new releases and enhancements to your financial planning tool.',
    keywords: 'release notes, changelog, updates, software versions, new features',
    canonical: `${BASE_URL}/releases`,
  },
  '/support': {
    title: 'Support - Solidly Finance Help & Contact',
    description: 'Get help with Solidly Finance. Contact our support team for assistance with your financial planning needs and product questions.',
    keywords: 'support, help, contact, customer service, financial planning help',
    canonical: `${BASE_URL}/support`,
  },
  '/privacy': {
    title: 'Privacy Policy - Solidly Finance',
    description: 'Learn how Solidly Finance protects your privacy and handles your financial data. Your financial information stays private and secure on your device.',
    keywords: 'privacy policy, data protection, financial privacy, secure finance app',
    canonical: `${BASE_URL}/privacy`,
  },
  '/terms': {
    title: 'Terms of Service - Solidly Finance',
    description: 'Read the terms of service for using Solidly Finance, including license terms and usage policies.',
    keywords: 'terms of service, terms and conditions, legal, usage terms',
    canonical: `${BASE_URL}/terms`,
  },
  '/demo': {
    title: 'Demo Video - See Solidly Finance in Action',
    description: 'Watch a comprehensive demo of Solidly Finance and see how it helps you chart your path to financial freedom with powerful planning and scenario tools.',
    keywords: 'demo video, product demo, financial planning software demo, solidly finance demo',
    canonical: `${BASE_URL}/demo`,
  },
};

/**
 * Generate SEO data for walkthrough routes dynamically
 * @param {string} id - Walkthrough ID (e.g., '001')
 * @returns {object} SEO metadata object
 */
const getWalkthroughSEO = (id) => {
  const walkthrough = WALKTHROUGHS[id];
  if (!walkthrough) return null;

  return {
    title: `${walkthrough.title} - Solidly Finance Walkthrough`,
    description: walkthrough.description,
    keywords: 'financial planning tutorial, personal finance walkthrough, wealth building guide, net worth planning',
    canonical: `${BASE_URL}/walkthrough/${id}`,
    ogTitle: `${walkthrough.title} - Solidly Finance Walkthrough`,
    ogDescription: walkthrough.description,
    ogImage: `${BASE_URL}${walkthrough.poster}`,
    twitterImage: `${BASE_URL}${walkthrough.poster}`,
  };
};

/**
 * Get SEO metadata for a specific route
 * @param {string} pathname - The current route pathname
 * @returns {object} SEO metadata object
 */
export const getSEOForRoute = (pathname) => {
  // Check if it's a walkthrough route
  const walkthroughMatch = pathname.match(/^\/walkthrough\/(\d+)$/);
  if (walkthroughMatch) {
    const walkthroughSEO = getWalkthroughSEO(walkthroughMatch[1]);
    if (walkthroughSEO) {
      return walkthroughSEO;
    }
  }

  // Otherwise use static route data
  const routeData = routeSEO[pathname] || routeSEO['/'];
  
  return {
    title: routeData.title,
    description: routeData.description,
    keywords: routeData.keywords || defaultMeta.keywords,
    canonical: routeData.canonical,
    ogTitle: routeData.ogTitle || routeData.title,
    ogDescription: routeData.ogDescription || routeData.description,
    ogImage: routeData.ogImage || defaultMeta.ogImage,
    twitterImage: routeData.twitterImage || defaultMeta.twitterImage,
  };
};

/**
 * Generate structured data for the organization
 * @returns {object} JSON-LD structured data
 */
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  "@type": ["SoftwareApplication","Product"],
  '@id': `${BASE_URL}#app`,
  name: SITE_NAME,
  applicationCategory: 'FinanceApplication',
  operatingSystem: 'Windows, macOS',
  description: 'Take control of your financial future with a private, offline app you own forever.',
  url: BASE_URL,
  image: defaultMeta.ogImage,
  offers: {
    '@type': 'Offer',
    price: 200.00,
    priceCurrency: 'USD',
    url: BASE_URL,
  },
  review: {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
    author: {
      '@type': 'Person',
      name: 'Early access user',
    },
    reviewBody: 'Reminds me of Mint, but it still exists and it is privacy conscious.',
  },
  publisher: {
    '@type': 'Organization',
    name: 'SolidlyAI',
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo192.png`,
    },
    url: 'https://get.solidly.ai',
  },
});

/**
 * Generate FAQ structured data for rich search results
 * @returns {object} JSON-LD FAQ schema
 */
export const getFAQSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Is my data synced to the cloud?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No, your financial data is never synced to the cloud. Solidly Finance is a desktop-only application that stores all your sensitive financial information locally on your device. Your data remains completely private and secure, with no internet connection required for core functionality.'
      }
    },
    {
      '@type': 'Question',
      name: 'What platforms does Solidly Finance support?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Solidly Finance is available as a desktop application for macOS and Windows. The app is built with Electron to provide a native desktop experience on both platforms, ensuring your financial data stays secure on your local device.'
      }
    },
    {
      '@type': 'Question',
      name: 'How secure is my financial information?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Your financial data is extremely secure with Solidly Finance. All sensitive information is encrypted at rest using industry-standard encryption, and since nothing is stored in the cloud, your data never leaves your device. You control your data completely with local-only storage.'
      }
    },
    {
      '@type': 'Question',
      name: 'Can I track multiple financial goals?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, Solidly Finance allows you to track multiple financial goals simultaneously, including debt payoff, building wealth milestones ($100K, $1M), and planning for FIRE (Financial Independence, Retire Early). You can create scenarios to compare different financial strategies.'
      }
    },
    {
      '@type': 'Question',
      name: 'Do I need to create an account?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No account creation is required. Solidly Finance is a completely local desktop application. Simply download, install, and start planning your financial future. Your privacy is protected as there are no user accounts, logins, or cloud synchronization.'
      }
    }
  ]
});

/**
 * Generate Organization structured data for Knowledge Panel
 * @returns {object} JSON-LD Organization schema
 */
export const getOrganizationKnowledgePanelSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'SolidlyAI',
  url: 'https://get.solidly.ai',
  logo: `${BASE_URL}/logo192.png`,
  sameAs: [
    'https://www.linkedin.com/company/solidlyai',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@solidly.ai',
    contactType: 'Customer Support'
  }
});
